// ============================================================
// TMR Portal do Administrador — responsive shell (sidebar + topbar)
// The sidebar is @tmr/portal-kit's: a rail that expands on hover on desktop and an off-canvas drawer at
// 900px and below. This shell supplies what is the admin's: its navigation, its avatar and the topbar.
// ============================================================
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PortalSidebar, SidebarProfile, SidebarToggle, useSidebarDrawer } from '@tmr/portal-kit';
import { useDesignTheme } from '@tmr/design-system';
import type { SidebarGroup } from '@tmr/portal-kit';
import type { TmrSession } from '@tmr/auth';
import { Icons, StrokeIcon } from './lib/icons';
import { Avatar } from './components/primitives';
import { SearchBox } from './components/ui';
import { useCardSpotlight } from './lib/spotlight';
import { NAV, filterNav, findNavItem } from './nav';
import { useHasPermission } from './data/PermissionsProvider';

export interface PortalShellProps {
  /** The operator behind the session cookie — present whichever way they signed in (F1). */
  operator: TmrSession;
  /** The context selector element, placed in the topbar; the shell renders it but does not own its data (F3a). */
  contextSelector?: ReactNode;
}

export function PortalShell({ operator, contextSelector }: PortalShellProps) {
  const [search, setSearch] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const drawer = useSidebarDrawer();
  const theme = useDesignTheme();
  useCardSpotlight(theme === 'site');

  const hasPermission = useHasPermission();
  const groups = filterNav(NAV, hasPermission);
  const displayName = operator.name || operator.email || 'Usuário';

  const current = findNavItem(groups.flatMap((g) => g.items), location.pathname);
  const curLabel = location.pathname.startsWith('/perfil') ? 'Meu perfil' : current?.label ?? '';
  const profileActive = location.pathname.startsWith('/perfil');

  // No `end`: an item stays marked on its sub-routes, so an Empresa's detail page keeps "Empresas" lit.
  const sidebarGroups: SidebarGroup[] = groups.map((g) => ({
    label: g.group,
    items: g.items.map((it) => ({
      key: it.key,
      label: it.label,
      path: it.path,
      icon: <StrokeIcon d={it.icon} size={18} />,
    })),
  }));

  return (
    <div className="pa-shell">
      <PortalSidebar
        caption="Portal administrativo"
        groups={sidebarGroups}
        open={drawer.open}
        onClose={drawer.hide}
        footer={
          <SidebarProfile
            avatar={<Avatar name={displayName} size={34} />}
            name={displayName}
            active={profileActive}
            onClick={() => navigate('/perfil')}
            trailing={<StrokeIcon d={Icons.chevronR} size={16} />}
          />
        }
      />

      {/* ---------- MAIN ---------- */}
      <div className="pa-main">
        <header className="pa-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, minWidth: 0 }}>
            <SidebarToggle icon={<StrokeIcon d={Icons.menu} size={18} />} expanded={drawer.open} onClick={drawer.show} />
            <span className="pa-crumb" style={{ color: 'var(--fg-subtle)' }}>Portal</span>
            <span className="pa-crumb-sep" style={{ color: 'var(--stone-300)' }}>/</span>
            <span className="pa-crumb-current" style={{ fontWeight: 500, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{curLabel}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {contextSelector}
            <span className="pa-topbar-search">
              <SearchBox value={search} onChange={setSearch} placeholder="Buscar no portal…" width={260} />
            </span>
            <button type="button" className="pa-icon-btn" style={{ position: 'relative' }} title="Notificações">
              <StrokeIcon d={Icons.bell} size={18} />
              <span style={{ position: 'absolute', top: 8, right: 9, width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)', border: '2px solid var(--cream-100)' }} />
            </button>
          </div>
        </header>

        <main className="pa-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
